import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

/**
 * NoteGenerationPanel - Shared component for generating notes from audio sessions
 *
 * @param {string} caseId - The case ID to generate notes for
 * @param {Array} audioSessions - Array of audio session objects
 * @param {Function} onNoteGenerated - Callback when note is successfully generated
 * @param {boolean} isOpen - Whether the panel is visible
 * @param {Function} onClose - Callback to close the panel
 * @param {string} providerId - The provider ID for filtering templates (required)
 * @param {string} healthSystemId - The health system ID (optional, for context)
 */
const NoteGenerationPanel = ({
  caseId,
  audioSessions = [],
  onNoteGenerated,
  isOpen = false,
  onClose,
  providerId = null,
  healthSystemId = null
}) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [selectedSessions, setSelectedSessions] = useState([]);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedTranscript, setUploadedTranscript] = useState('');
  const [useUploadedTranscript, setUseUploadedTranscript] = useState(false);

  // Organized template groups for dropdown
  const [providerTemplates, setProviderTemplates] = useState([]);
  const [healthSystemTemplates, setHealthSystemTemplates] = useState([]);
  const [globalTemplates, setGlobalTemplates] = useState([]);
  const [providerInfo, setProviderInfo] = useState(null);

  // Note generation options
  const [brevity, setBrevity] = useState('standard');
  const [customInstructions, setCustomInstructions] = useState('');
  const [sections, setSections] = useState({
    reasonForVisit: true,
    historyOfPresentIllness: true,
    reviewOfSystems: true,
    physicalExam: true,
    assessmentAndPlan: true,
    codingSuggestions: true
  });

  // Load templates on mount or when providerId changes
  useEffect(() => {
    if (isOpen && providerId) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, providerId]);

  // Reset selections when panel closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedSessions([]);
      setGenerationError(null);
      setBrevity('standard');
      setCustomInstructions('');
      setUploadedFile(null);
      setUploadedTranscript('');
      setUseUploadedTranscript(false);
      setSections({
        reasonForVisit: true,
        historyOfPresentIllness: true,
        reviewOfSystems: true,
        physicalExam: true,
        assessmentAndPlan: true,
        codingSuggestions: true
      });
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    if (!providerId) {
      setGenerationError('Provider information not available. Cannot load templates.');
      return;
    }

    setLoadingTemplates(true);
    setGenerationError(null);
    try {
      // Make parallel requests for provider/health-system templates and global templates
      const [providerResponse, globalResponse] = await Promise.all([
        adminApi.get(`/provider/note/templates/?provider_id=${providerId}`),
        adminApi.get('/system/note/templates/')
      ]);

      let providerTemps = [];
      let healthSystemTemps = [];
      let globalTemps = [];

      // Extract provider and health system templates
      if (providerResponse.data && providerResponse.data.success) {
        providerTemps = providerResponse.data.provider_templates?.templates || [];
        healthSystemTemps = providerResponse.data.health_system_templates?.templates || [];

        // Extract provider info for label
        const provider = providerResponse.data.provider_templates?.provider;
        if (provider) {
          setProviderInfo(provider);
        }
      }

      // Extract global templates (not wrapped in success object)
      if (globalResponse.data && Array.isArray(globalResponse.data)) {
        globalTemps = globalResponse.data;
      }

      // Store organized groups for dropdown
      setProviderTemplates(providerTemps);
      setHealthSystemTemplates(healthSystemTemps);
      setGlobalTemplates(globalTemps);

      // Combine all templates for selection logic
      const allTemplates = [...providerTemps, ...healthSystemTemps, ...globalTemps];
      setTemplates(allTemplates);

      if (allTemplates.length > 0) {
        // Prefer default template if available, otherwise use first template
        const defaultTemplate = allTemplates.find(t => t.is_default);
        setSelectedTemplate(defaultTemplate || allTemplates[0]);
      } else {
        setSelectedTemplate(null);
      }
    } catch (error) {
      setGenerationError(error.response?.data?.error || 'Failed to load note templates');
      setTemplates([]);
      setProviderTemplates([]);
      setHealthSystemTemplates([]);
      setGlobalTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSessionToggle = (sessionId) => {
    setSelectedSessions(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      } else {
        return [...prev, sessionId];
      }
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Check file type
    const validTypes = ['text/plain', 'text/rtf', 'application/rtf'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.txt') && !file.name.endsWith('.rtf')) {
      setGenerationError('Please upload a plain text (.txt) or RTF (.rtf) file');
      return;
    }
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setGenerationError('File size must be less than 5MB');
      return;
    }
    try {
      const text = await file.text();
      setUploadedFile(file);
      setUploadedTranscript(text);
      setUseUploadedTranscript(true);
      setGenerationError(null);
    } catch (error) {
      setGenerationError('Failed to read file. Please try again.');
      setUploadedFile(null);
      setUploadedTranscript('');
    }
  };
  const handleRemoveUploadedFile = () => {
    setUploadedFile(null);
    setUploadedTranscript('');
    setUseUploadedTranscript(false);
  };

  const handleGenerateNote = async () => {
    if (!selectedTemplate) {
      setGenerationError('Please select a note template');
      return;
    }

    // Check if either audio sessions are selected or transcript is uploaded
    if (!useUploadedTranscript && selectedSessions.length === 0) {
      setGenerationError('Please select at least one audio session or upload a transcript');
      return;
    }

    if (useUploadedTranscript && !uploadedTranscript) {
      setGenerationError('Please upload a transcript file');
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);

    try {
      const payload = {
        case_id: caseId,
        template_id: selectedTemplate.id,
        transcript_ids: useUploadedTranscript ? [] : selectedSessions, // Empty array if using uploaded transcript
        provider_preferences: {
          brevity: brevity,
          specialty: providerInfo?.specialty || ''
        },
        sections: sections,
        custom_instructions: customInstructions,
        case_info: {
          patient_name: '',
          chief_complaint: '',
          visit_type: ''
        }
      };

      // Add uploaded_transcript if user uploaded a file
      if (useUploadedTranscript) {
        payload.uploaded_transcript = uploadedTranscript;
        payload.uploaded_transcript_filename = uploadedFile.name;  // Include filename for S3 storage
      }

      const response = await adminApi.post('/provider/audio/note/generate/', payload);

      if (response.data && response.data.success) {
        // Notify parent component
        if (onNoteGenerated) {
          onNoteGenerated(response.data);
        }
        // Close panel
        if (onClose) {
          onClose();
        }
        // Reset selections
        setSelectedSessions([]);
      } else {
        throw new Error(response.data?.error || 'Failed to generate note');
      }
    } catch (error) {
      setGenerationError(error.response?.data?.error || error.message || 'Failed to generate note');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="card mb-3 bg-light">
      <div className="card-body">
        <h6 className="card-subtitle mb-3">
          <i className="fas fa-magic me-2"></i>
          Generate Note from Audio
        </h6>

        {generationError && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {generationError}
            <button
              type="button"
              className="btn-close"
              onClick={() => setGenerationError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Template Selection */}
        <div className="mb-3">
          <label className="form-label small fw-bold">Note Template</label>
          {!providerId ? (
            <div className="text-muted small">Provider information not available</div>
          ) : loadingTemplates ? (
            <div className="text-muted small">Loading templates...</div>
          ) : (
            <select
              className="form-select form-select-sm"
              value={selectedTemplate?.id || ''}
              onChange={(e) => {
                const template = templates.find(t => t.id === e.target.value);
                setSelectedTemplate(template);
              }}
            >
              <option value="">Select a template...</option>

              {/* Provider Templates */}
              <optgroup label={providerInfo ? `Templates for ${providerInfo.name}` : "Provider Templates"}>
                {providerTemplates.length > 0 ? (
                  providerTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                      {template.is_default ? ' (Default)' : ''}
                      {template.is_favorite ? ' ⭐' : ''}
                    </option>
                  ))
                ) : (
                  <option disabled>0 templates available</option>
                )}
              </optgroup>

              {/* Health System Templates */}
              <optgroup label="Health System Templates">
                {healthSystemTemplates.length > 0 ? (
                  healthSystemTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))
                ) : (
                  <option disabled>0 templates available</option>
                )}
              </optgroup>

              {/* Global Templates */}
              <optgroup label="Global Templates">
                {globalTemplates.length > 0 ? (
                  globalTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))
                ) : (
                  <option disabled>0 templates available</option>
                )}
              </optgroup>
            </select>
          )}
        </div>

        {/* Brevity Setting */}
        <div className="mb-3">
          <label className="form-label small fw-bold">Note Detail Level</label>
          <select
            className="form-select form-select-sm"
            value={brevity}
            onChange={(e) => setBrevity(e.target.value)}
          >
            <option value="brief">Brief - Concise, key findings only</option>
            <option value="standard">Standard - Balanced documentation</option>
            <option value="detailed">Detailed - Comprehensive documentation</option>
          </select>
        </div>

        {/* Sections to Include */}
        <div className="mb-3">
          <label className="form-label small fw-bold">Sections to Include</label>
          <div className="border rounded p-2">
            {Object.entries({
              reasonForVisit: 'Chief Complaint / Reason for Visit',
              historyOfPresentIllness: 'History of Present Illness',
              reviewOfSystems: 'Review of Systems',
              physicalExam: 'Physical Examination',
              assessmentAndPlan: 'Assessment and Plan',
              codingSuggestions: 'Coding/Billing Suggestions'
            }).map(([key, label]) => (
              <div key={key} className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`section-${key}`}
                  checked={sections[key]}
                  onChange={(e) => setSections(prev => ({ ...prev, [key]: e.target.checked }))}
                />
                <label className="form-check-label small" htmlFor={`section-${key}`}>
                  {label}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Instructions */}
        <div className="mb-3">
          <label className="form-label small fw-bold">Custom Instructions (Optional)</label>
          <textarea
            className="form-control form-control-sm"
            rows="3"
            placeholder="Add any specific instructions for note generation..."
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
          />
          <div className="form-text">Provide additional context or requirements for the generated note</div>
        </div>

        {/* Transcript Source Selection */}
        <div className="mb-3">
          <label className="form-label small fw-bold">Transcript Source</label>
          <div className="border rounded p-2">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="useAudioSessions"
                checked={!useUploadedTranscript}
                onChange={() => setUseUploadedTranscript(false)}
              />
              <label className="form-check-label small" htmlFor="useAudioSessions">
                Use existing audio sessions
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="useUploadedFile"
                checked={useUploadedTranscript}
                onChange={() => setUseUploadedTranscript(true)}
              />
              <label className="form-check-label small" htmlFor="useUploadedFile">
                Upload transcript file
              </label>
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        {useUploadedTranscript && (
          <div className="mb-3">
            <label className="form-label small fw-bold">Upload Transcript</label>
            {uploadedFile ? (
              <div className="border rounded p-2 bg-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <i className="fas fa-file-alt text-primary me-2"></i>
                    <span className="small">{uploadedFile.name}</span>
                    <span className="text-muted small ms-2">
                      ({(uploadedFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRemoveUploadedFile}
                    title="Remove file"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="mt-2">
                  <small className="text-muted">
                    Preview: {uploadedTranscript.substring(0, 100)}
                    {uploadedTranscript.length > 100 ? '...' : ''}
                  </small>
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  className="form-control form-control-sm"
                  accept=".txt,.rtf"
                  onChange={handleFileUpload}
                />
                <div className="form-text">
                  Upload a plain text (.txt) or RTF (.rtf) file containing the visit transcript. Max size: 5MB
                </div>
              </div>
            )}
          </div>
        )}

        {/* Session Selection - Only show if not using uploaded transcript */}
        {!useUploadedTranscript && (
          <div className="mb-3">
            <label className="form-label small fw-bold">Select Audio Sessions</label>
            <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {audioSessions.length === 0 ? (
                <div className="text-muted small text-center py-2">
                  No audio sessions available
                </div>
              ) : (
                audioSessions.map((session, index) => (
                  <div key={session.session_id} className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`session-${session.session_id}`}
                      checked={selectedSessions.includes(session.session_id)}
                      onChange={() => handleSessionToggle(session.session_id)}
                    />
                    <label className="form-check-label small" htmlFor={`session-${session.session_id}`}>
                      Session {index + 1} - {new Date(session.created_at).toLocaleString()}
                      <span className="text-muted ms-2">
                        ({session.segment_count} segment{session.segment_count !== 1 ? 's' : ''})
                      </span>
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-flex gap-2">
          <button
            className="btn btn-primary btn-sm"
            onClick={handleGenerateNote}
            disabled={
              isGenerating ||
              !selectedTemplate ||
              (!useUploadedTranscript && selectedSessions.length === 0) ||
              (useUploadedTranscript && !uploadedTranscript)
            }
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Generating...
              </>
            ) : (
              <>
                <i className="fas fa-magic me-1"></i>
                Generate Note
              </>
            )}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              if (onClose) {
                onClose();
              }
              setGenerationError(null);
              setSelectedSessions([]);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoteGenerationPanel;
